import { Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { RHFTextField, RHFSelect } from '@Components/hook-form';
import {
  FreeBetAccessStatusEnum,
  useGetProviderConfigsLazyQuery,
  useGetProvidersQuery,
  usePaginatedFreeBetsCampaignsLazyQuery,
} from '@Src/generated.graphql';
import { ChangeEvent, useEffect, useMemo } from 'react';
import { Controller, useFormContext, useFormState, useWatch } from 'react-hook-form';
import {
  createFreebetsSchema,
  useManageCreateFreebetsSchema,
  type CreateFreebetsFormState,
} from '@Forms/freebets/CreateFreebetsFormContext';
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';
import { useCreateFreebets } from '@Modal/hooks/freebets/useCreateFreebets';

const formatToLocalDateTime = (input?: string | Date | null) => {
  if (!input) {
    return '';
  }

  const date = new Date(input);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const CreateFreebetsContent = () => {
  const { control, setValue, getValues } = useFormContext<CreateFreebetsFormState>();
  const providerId = useWatch({ control, name: 'providerId' });
  const file = useWatch({ control, name: 'file' });

  const selectedCurrency = useWatch({ control, name: 'currency' });
  const { errors } = useFormState({ control });
  const { setSchema } = useManageCreateFreebetsSchema();
  const { userPermissions, currentUser } = useAuthorization();
  const { showCreateFreebetsForm } = useCreateFreebets();
  const permissionToViewProviders = userPermissions?.includes('CAN_VIEW_PROVIDERS');
  const { data: providersData } = useGetProvidersQuery({ skip: !permissionToViewProviders });
  const [getCampaigns, { data: campaignsData }] = usePaginatedFreeBetsCampaignsLazyQuery();
  const [getProviderConfigs, { data: providerConfigsData }] = useGetProviderConfigsLazyQuery();

  const minDate = useMemo(() => {
    const today = new Date();
    return formatToLocalDateTime(
      new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)
    );
  }, []);

  const providerOptions = useMemo(
    () =>
      permissionToViewProviders
        ? (providersData?.providers ?? []).map((provider) => ({
            id: provider.id,
            name: provider.name,
          }))
        : (currentUser?.providerIds ?? []).map((providerId) => ({
            id: providerId,
            name: providerId,
          })),
    [currentUser?.providerIds, permissionToViewProviders, providersData?.providers]
  );
  const hasSingleProvider = providerOptions.length === 1;
  const fileError = errors.file?.message;

  useEffect(() => {
    if (!hasSingleProvider) {
      return;
    }

    const singleProviderId = providerOptions[0]?.id;
    const selectedProviderId = getValues('providerId');

    if (singleProviderId && !selectedProviderId) {
      setValue('providerId', singleProviderId, { shouldValidate: true, shouldDirty: false });
    }
  }, [getValues, hasSingleProvider, providerOptions, setValue]);

  useEffect(() => {
    if (!providerId) {
      return;
    }

    getCampaigns({
      fetchPolicy: 'network-only',
      variables: {
        limit: 100,
        page: 1,
        search: '',
        filter: { providerId, status: FreeBetAccessStatusEnum.Active, validCampaigns: true },
      },
    });
    getProviderConfigs({
      variables: { id: providerId },
    });
  }, [getCampaigns, getProviderConfigs, providerId]);

  useEffect(() => {
    if (!showCreateFreebetsForm || !providerId) {
      return;
    }

    void getCampaigns({
      fetchPolicy: 'network-only',
      variables: {
        limit: 100,
        page: 1,
        search: '',
        filter: { providerId, status: FreeBetAccessStatusEnum.Active, validCampaigns: true },
      },
    });
  }, [getCampaigns, providerId, showCreateFreebetsForm]);

  useEffect(() => {
    const selectedProviderConfig = providerConfigsData?.providerConfigs?.find(
      (config) => config.currency === selectedCurrency
    );

    if (!selectedProviderConfig) {
      setSchema(createFreebetsSchema());
      return;
    }

    setSchema(
      createFreebetsSchema({
        minAutoCashout: selectedProviderConfig.minAutoCashout,
        maxAutoCashout: selectedProviderConfig.maxAutoCashout,
        minBet: selectedProviderConfig.minBet,
        maxBet: selectedProviderConfig.maxBet,
      })
    );
  }, [providerConfigsData?.providerConfigs, selectedCurrency, setSchema]);

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    setValue('file', selectedFile, { shouldValidate: true, shouldDirty: true });
    event.target.value = '';
  };

  return (
    <Stack gap={2}>
      <RHFSelect name="providerId" label="Provider" type="text" disabled={hasSingleProvider}>
        {providerOptions.map((provider) => (
          <MenuItem key={provider.id} value={provider.id}>
            {provider.name}
          </MenuItem>
        ))}
      </RHFSelect>
      <RHFSelect name="campaignId" label="Campaign" type="text">
        {campaignsData?.paginatedFreeBetsCampaigns?.docs?.map((campaign) => (
          <MenuItem key={campaign._id} value={campaign._id}>
            {campaign.description}
          </MenuItem>
        ))}
      </RHFSelect>
      <RHFSelect name="currency" label="Currency" type="text">
        {providerConfigsData?.providerConfigs?.map((provider) => (
          <MenuItem key={provider.currency} value={provider.currency}>
            {provider.currency}
          </MenuItem>
        ))}
      </RHFSelect>
      <Stack gap={1}>
        <Button component="label" variant="outlined" sx={{ width: '100%', cursor: 'pointer' }}>
          Upload Player IDs (XLSX/XLS)
          <input
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
        </Button>
        {file ? <Typography variant="body2">{file.name}</Typography> : null}
        {fileError ? (
          <Typography variant="caption" color="error">
            {fileError}
          </Typography>
        ) : null}
      </Stack>
      <Controller
        name="validityDate"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            size="small"
            label="Validity Date & Time"
            type="datetime-local"
            data-at-text-field="FreebetValidityDate"
            value={formatToLocalDateTime(field.value)}
            onChange={(event) =>
              field.onChange(event.target.value ? new Date(event.target.value) : null)
            }
            fullWidth
            error={!!error}
            helperText={error?.message}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: minDate }}
          />
        )}
      />
      <RHFTextField name="count" label="Count" type="number" />
      <RHFTextField name="minMultiplier" label="Min Multiplier" type="number" />
      <RHFTextField name="betNominal" label="Bet Nominal" type="number" />
    </Stack>
  );
};
